// Lightweight bridge to detect external app cart changes (Essential Upsell)
// and notify the theme to refresh cart sections and open the drawer.
// This file intentionally keeps behavior conservative to avoid re-entrancy loops.
(function () {
  if (!window.fetch) return;

  let suppressNotify = false;
  const inSuppression = () => suppressNotify === true;
  let shouldOpenDrawerNext = false;
  // Enable runtime debug logging by setting `window.cartBridgeDebug = true` in console
  // Enable runtime debug logging by setting `window.cartBridgeDebug = true` in console
  // Use a function so the flag can be enabled after this script loads.
  const DBG = () => !!(window && window.cartBridgeDebug);

  // If the bridge recently scheduled a reload, open the drawer on page load
  try {
    const lastReload = sessionStorage.getItem('cartBridgeLastReload');
    if (lastReload && Date.now() - parseInt(lastReload, 10) < 15000) {
      // remove the flag so subsequent navigations don't re-open
      sessionStorage.removeItem('cartBridgeLastReload');
      const openDrawer = () => {
        try {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        } catch (e) {}
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', openDrawer);
      } else {
        // document already ready
        setTimeout(openDrawer, 50);
      }
    }
  } catch (e) {}

  // Add a capture-phase listener to catch clicks that other handlers may stopPropagation on.
  try {
    document.addEventListener(
      'click',
      (event) => {
        try {
          const addSelector = '[data-essential-upsell-element="add-to-cart-button"]';
          const specific = event.target.closest(
            `dialog[data-essential-upsell-offer-type="FREQUENTLY_BOUGHT_TOGETHER"] ${addSelector}`,
          );

          const genericMatch = event.target.closest(addSelector);
          const generic =
            genericMatch &&
            (genericMatch.tagName === 'BUTTON' || genericMatch.matches('a,button,input[type="button"],[role="button"]'))
              ? genericMatch
              : null;

          const inCartMatch = event.target.closest(`#CartDrawer ${addSelector}, #main-cart-items ${addSelector}`);
          const inCartAdd =
            inCartMatch &&
            (inCartMatch.tagName === 'BUTTON' || inCartMatch.matches('a,button,input[type="button"],[role="button"]'))
              ? inCartMatch
              : null;

          const funnelAncestor =
            (generic || inCartAdd) &&
            ((generic || inCartAdd).closest
              ? (generic || inCartAdd).closest(
                  '[data-essential-upsell-funnel-content-offer-type="CROSS_SELL"], [data-essential-upsell-funnel-content-offer-type="PRODUCT_ADDON"]',
                )
              : null);

          const shouldTrigger = !!(specific || generic || inCartAdd || funnelAncestor);

          if (shouldTrigger) {
            if (DBG()) {
              try {
                console.debug('cart-bridge: capture click detected', {
                  phase: 'capture',
                  specific: !!specific,
                  generic: !!genericMatch,
                  genericResolved: !!generic,
                  inCartAdd: !!inCartAdd,
                  funnelAncestor: !!funnelAncestor,
                });
              } catch (e) {}
            }

            if (inCartAdd || funnelAncestor) shouldOpenDrawerNext = true;
            try {
              const last = parseInt(sessionStorage.getItem('cartBridgeLastReload') || '0', 10);
              if (Date.now() - last < 5000) return; // recent action scheduled
              sessionStorage.setItem('cartBridgeLastReload', String(Date.now()));
              suppressNotify = true;
              setTimeout(() => {
                try {
                  window.location.reload();
                } catch (e) {
                  window.location.href = window.location.href;
                }
              }, 700);
            } catch (e) {}
          }
        } catch (e) {}
      },
      true,
    );
  } catch (e) {}

  const shouldHandleUrl = (url) => {
    try {
      if (!url) return false;
      const u = String(url);
      if (window.routes) {
        if (window.routes.cart_add_url && u.includes(window.routes.cart_add_url)) return true;
        if (window.routes.cart_change_url && u.includes(window.routes.cart_change_url)) return true;
        if (window.routes.cart_update_url && u.includes(window.routes.cart_update_url)) return true;
        // do NOT treat routes.cart_url as external (theme uses it)
      }

      return (
        u.includes('/cart/add') ||
        u.includes('/cart/change') ||
        u.includes('/cart/update') ||
        u.includes('/cart/add.js') ||
        u.includes('/cart.js')
      );
    } catch (e) {
      return false;
    }
  };

  const notifyTheme = () => {
    try {
      suppressNotify = true;
      setTimeout(() => {
        suppressNotify = false;
      }, 2500);

      setTimeout(() => {
        try {
          const lastReload = sessionStorage.getItem('cartBridgeLastReload');
          if (lastReload && Date.now() - parseInt(lastReload, 10) < 10000) return;

          try {
            const p =
              typeof publish === 'function'
                ? publish(PUB_SUB_EVENTS.cartUpdate, { source: 'external' })
                : Promise.resolve();
            p.then(() => {
              try {
                if (shouldOpenDrawerNext) {
                  const drawer = document.querySelector('cart-drawer');
                  if (drawer && typeof drawer.open === 'function') drawer.open();
                }
              } catch (e) {}
              shouldOpenDrawerNext = false;
            }).catch(() => {
              shouldOpenDrawerNext = false;
            });
          } catch (e) {
            shouldOpenDrawerNext = false;
          }
        } catch (e) {}
      }, 350);
    } catch (e) {}
  };

  // Monkeypatch fetch
  const _fetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    try {
      const url = typeof input === 'string' ? input : input && input.url;
      const method = (init && init.method) || (input && input.method) || 'GET';
      const handled = shouldHandleUrl(url);
      const promise = _fetch(input, init);

      if (handled && method && method.toUpperCase() !== 'GET' && !inSuppression()) {
        promise
          .then((res) => {
            try {
              res
                .clone()
                .text()
                .catch(() => null)
                .finally(() => notifyTheme());
            } catch (e) {
              notifyTheme();
            }
          })
          .catch(() => {
            notifyTheme();
          });
      }

      return promise;
    } catch (e) {
      return _fetch(input, init);
    }
  };

  // Monkeypatch XHR
  if (window.XMLHttpRequest) {
    const XHR = window.XMLHttpRequest;
    const _open = XHR.prototype.open;
    const _send = XHR.prototype.send;

    XHR.prototype.open = function (method, url) {
      try {
        this._cart_bridge_url = url;
        this._cart_bridge_method = method;
      } catch (e) {}
      return _open.apply(this, arguments);
    };

    XHR.prototype.send = function (body) {
      try {
        this.addEventListener('load', function () {
          try {
            const method = (this._cart_bridge_method || 'GET').toUpperCase();
            if (!inSuppression() && method !== 'GET' && shouldHandleUrl(this._cart_bridge_url)) notifyTheme();
          } catch (e) {}
        });
      } catch (e) {}
      return _send.apply(this, arguments);
    };
  }

  // Click delegation: Essential Upsell add-to-cart buttons -> reload page
  try {
    document.addEventListener('click', (event) => {
      try {
        const addSelector = '[data-essential-upsell-element="add-to-cart-button"]';
        const specific = event.target.closest(
          `dialog[data-essential-upsell-offer-type="FREQUENTLY_BOUGHT_TOGETHER"] ${addSelector}`,
        );

        // Ensure the matched element is an actual clickable button (not a container).
        const genericMatch = event.target.closest(addSelector);
        const generic =
          genericMatch &&
          (genericMatch.tagName === 'BUTTON' || genericMatch.matches('a,button,input[type="button"],[role="button"]'))
            ? genericMatch
            : null;

        const inCartMatch = event.target.closest(`#CartDrawer ${addSelector}, #main-cart-items ${addSelector}`);
        const inCartAdd =
          inCartMatch &&
          (inCartMatch.tagName === 'BUTTON' || inCartMatch.matches('a,button,input[type="button"],[role="button"]'))
            ? inCartMatch
            : null;

        // Determine whether this click should trigger a reload.
        // Always trigger for the frequent-bought dialog (`specific`) or
        // explicit add buttons (`generic`). Additionally trigger when an
        // add button sits inside certain funnel content offer types
        // (CROSS_SELL or PRODUCT_ADDON), even if rendered inside the cart.
        const funnelAncestor =
          (generic || inCartAdd) &&
          ((generic || inCartAdd).closest
            ? (generic || inCartAdd).closest(
                '[data-essential-upsell-funnel-content-offer-type="CROSS_SELL"], [data-essential-upsell-funnel-content-offer-type="PRODUCT_ADDON"]',
              )
            : null);

        const shouldTrigger = !!(specific || generic || inCartAdd || funnelAncestor);

        if (shouldTrigger) {
          if (DBG()) {
            try {
              console.debug('cart-bridge: click detected', {
                specific: !!specific,
                generic: !!genericMatch,
                genericResolved: !!generic,
                inCartAdd: !!inCartAdd,
                funnelAncestor: !!funnelAncestor,
                matchedElement: (generic || inCartAdd || specific) && (generic || inCartAdd || specific).tagName,
                dataset: (generic || inCartAdd || specific) && (generic || inCartAdd || specific).dataset,
              });
            } catch (e) {}
          }

          if (inCartAdd || funnelAncestor) shouldOpenDrawerNext = true;
          try {
            const last = parseInt(sessionStorage.getItem('cartBridgeLastReload') || '0', 10);
            if (Date.now() - last < 5000) return; // recent action scheduled
            sessionStorage.setItem('cartBridgeLastReload', String(Date.now()));
            suppressNotify = true;
            // small delay to let the app do its network work
            setTimeout(() => {
              try {
                window.location.reload();
              } catch (e) {
                window.location.href = window.location.href;
              }
            }, 700);
          } catch (e) {}
        }
      } catch (e) {}
    });
  } catch (e) {}

  // Expose manual trigger for debugging
  try {
    window.cartBridgeNotify = function () {
      if (!inSuppression()) notifyTheme();
    };
  } catch (e) {}
})();
